import { useAction, useAtom } from '@reatom/npm-react'
import i18next from 'i18next'
import React from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { Trans } from 'react-i18next'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { Result } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'
import {
  TelegramUrlFormControl,
  WebsiteUrlFormControl,
  XUrlFormControl,
} from '@root/shared/form-control'
import { useTranslation } from '@root/shared/localization'
import { AvatarPicker, Input, Textarea } from '@root/shared/ui-kit'
import {
  WebsiteIcon,
  WarningTriangleIcon,
  TelegramConturedMonoIcon,
  XConturedMonoIcon,
} from '@root/shared/ui-kit/icons'

import { reatomCreateCurrencyForm } from '../atoms/reatom-currency-create-form'

import { CurrencyPreview } from './currency-preview'
import { FormSection } from './form-section'

type TokenCreateFormProps = {
  onDone: () => void
}

type CurrencyCreateFormValues = {
  readonly avatar: File | undefined
  readonly name: string
  readonly symbol: string
  readonly description: string
  readonly websiteUrl: string
  readonly telegramUrl: string
  readonly xUrl: string
}

const Form = styled.form`
  display: grid;
  gap: 15px;
`

const Root = styled.div``

const AVATAR_EXTENSIONS = ['jpeg', 'png', 'webp', 'gif']
const AVATAR_FILESIZE_Mb = 5
const AVATAR_FILESIZE_b = AVATAR_FILESIZE_Mb * 1024 * 1024
const DESCRIPTION_LENGTH = 500

const validateFileSize = (size: number) => (file?: File | undefined) => {
  if (!file) {
    return false
  }

  return file.size > size ? i18next.t('validation.file-too-large') : true
}

export const CurrencyCreateForm: React.FC<TokenCreateFormProps> = (props) => {
  const { t } = useTranslation()
  // eslint-disable-next-line @reatom/unit-naming-rule
  const model = useMemo(() => reatomCreateCurrencyForm(), [])
  const [requestState] = useAtom(model.requestState)
  const executeRequest = useAction(model.requestState.execute)

  const form = useForm<CurrencyCreateFormValues>({
    mode: 'all',
    defaultValues: {
      avatar: undefined,
      description: '',
      name: '',
      symbol: '',
      telegramUrl: '',
      websiteUrl: '',
      xUrl: '',
    },
  })
  const formValues = form.watch()

  const handleSubmit = form.handleSubmit(async (values) => {
    const result = await executeRequest(values)

    if (Result.isFailure(result)) {
      return
    }

    props.onDone()
  })

  return (
    <Root>
      <FormProvider {...form}>
        <Form onSubmit={handleSubmit}>
          <FormSection>
            <CurrencyPreview
              image={
                <Controller
                  control={form.control}
                  rules={{
                    required: t('validation.required'),
                    validate: validateFileSize(AVATAR_FILESIZE_b),
                  }}
                  name="avatar"
                  render={({ field, fieldState }) => (
                    <AvatarPicker
                      {...field}
                      required={false}
                      acceptableExtensions={AVATAR_EXTENSIONS}
                      errorMessage={fieldState.error?.message}
                    />
                  )}
                />
              }
              primary={formValues.name}
              secondary={formValues.symbol}
              tertiary={formValues.description}
              links={[
                URL.canParse(formValues.websiteUrl) ? (
                  <WebsiteIcon height={15} width={15} />
                ) : undefined,
                URL.canParse(formValues.telegramUrl) ? (
                  <TelegramConturedMonoIcon height={15} width={15} />
                ) : undefined,
                URL.canParse(formValues.xUrl) ? (
                  <XConturedMonoIcon height={15} width={15} />
                ) : undefined,
              ].filter((x) => !!x)}
            />
            <Tip>
              {[...AVATAR_EXTENSIONS, `file < ${AVATAR_FILESIZE_Mb}Mb`].join(
                ' | ',
              )}
            </Tip>
          </FormSection>
          <FormSection>
            <div>
              <Controller
                rules={{ required: t('validation.required') }}
                control={form.control}
                name="name"
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    label={t('common.name')}
                    required
                    size="m"
                    error={fieldState.error?.message}
                  />
                )}
              />
            </div>
            <div>
              <Controller
                rules={{ required: t('validation.required') }}
                control={form.control}
                name="symbol"
                render={({ field, fieldState }) => {
                  return (
                    <Input
                      {...field}
                      label={t('common.symbol')}
                      required
                      error={fieldState.error?.message}
                      size="m"
                    />
                  )
                }}
              />
            </div>
            <div>
              <Controller
                rules={{ required: t('validation.required') }}
                control={form.control}
                name="description"
                render={({ field, fieldState }) => (
                  <Textarea
                    {...field}
                    required
                    emojiPicker
                    label={t('common.description')}
                    errorMessage={fieldState.error?.message}
                    maxLength={DESCRIPTION_LENGTH}
                    size="m"
                  />
                )}
              />
            </div>
          </FormSection>
          <FormSection header={t('common.optional').toUpperCase()}>
            <>
              <div>
                <WebsiteUrlFormControl<CurrencyCreateFormValues> />
              </div>
              <div>
                <TelegramUrlFormControl<CurrencyCreateFormValues> />
              </div>
              <div>
                <XUrlFormControl<CurrencyCreateFormValues> />
              </div>
            </>
          </FormSection>
          <Button
            disabled={!form.formState.isValid}
            busy={requestState.loading}
            type="submit"
            variant="solid"
            color="primary"
            radius="rounded"
          >
            {t('currency.create-cta')}
          </Button>
          <FormFooter>
            <Warning text={t('currency.create-tip')} />
            <Subtip>
              <Trans
                i18nKey="currency.create-subtip"
                components={[<b key={0} />]}
              />
            </Subtip>
          </FormFooter>
        </Form>
      </FormProvider>
    </Root>
  )
}

const FormFooter = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 2px;
  text-align: center;
`

type WarningProps = {
  text: string
}
const Warning = (props: WarningProps) => (
  <WarningRoot>
    <span>
      <WarningTriangleIcon height={14} width={14} />
    </span>
    <span>{props.text}</span>
  </WarningRoot>
)
const WarningRoot = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  gap: 2px;
  color: var(--warning-9);
`
const Subtip = styled.span`
  font-size: 11px;
  color: var(--background-1);

  b {
    color: var(--appearance-inv-1);
  }
`
const Tip = styled.div`
  text-align: center;
  font-size: 10px;
  color: var(--text-7);
`
